import { useState } from "react";
import {
  CFormInput,
  CFormTextarea,
  CFormCheck,
  CFormSelect,
  CButton,
  CRow,
  CCol,
  CBadge,
  CCollapse,
} from "@coreui/react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { Field, SectionCard } from "../__sections";

import { colors } from "../../../config/theme";
import PlusIcon from "../../../components/icons/PlusIcon";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import ScheduleEditor from "../__sections/scheduleEditor";
import { emptyOption } from "../../../helpers/types";
import { formatDuration } from "../../../helpers/general";
import ChevronIcon from "../../../components/icons/ChevroIcon";

const SCHEDULE_MODE_LABELS: Record<string, string> = {
  default: "Calendrier par défaut",
  existing: "Calendrier réutilisé",
  custom: "Calendrier personnalisé",
};

export default function Options() {
  const { register, control, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "options",
  });

  const topLevelSchedules: any[] = watch("schedules") || [];
  const reusableSchedules = topLevelSchedules.filter((s: any) => s?.id);

  const [openIndex, setOpenIndex] = useState<number | null>(
    fields.length === 1 ? 0 : null,
  );

  const toggle = (index: number) =>
    setOpenIndex((prev) => (prev === index ? null : index));

  const handleAdd = () => {
    append(emptyOption());
    setOpenIndex(fields.length); // open the newly added one
  };

  const handleRemove = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    remove(index);
    if (openIndex === index) setOpenIndex(null);
    else if (openIndex !== null && openIndex > index)
      setOpenIndex(openIndex - 1);
  };

  return (
    <div className="animate-fade-in">
      <SectionCard
        title="Options"
        subtitle="Les options sont des variantes réservables de l'activité (ex: 'Visite privée', 'Petit groupe'). Chacune peut garder le calendrier par défaut ou définir le sien."
      >
        {fields.length === 0 && (
          <div
            className="text-center py-5 mb-3 d-flex flex-column align-items-center gap-2"
            style={{
              border: "1px dashed #ddd",
              borderRadius: "12px",
              color: "#999",
            }}
          >
            <div style={{ fontSize: "1.75rem" }}>🎟️</div>
            <div className="fw-semibold" style={{ color: "#777" }}>
              Aucune option pour le moment
            </div>
            <div className="small" style={{ maxWidth: 360 }}>
              Ajoutez une variante réservable de l'activité, comme une visite
              privée ou un petit groupe.
            </div>
          </div>
        )}

        <div className="d-flex flex-column gap-2 mb-3">
          {fields.map((field, index) => {
            const isOpen = openIndex === index;
            const scheduleMode = watch(`options.${index}.scheduleMode`);
            const title = watch(`options.${index}.title`);
            const durationMinutes = watch(`options.${index}.durationMinutes`);
            const durationUnit = watch(`options.${index}.durationUnit`);
            const wheelchairAccessible = watch(
              `options.${index}.meta.wheelchairAccessible`,
            );
            const isPrivate = watch(`options.${index}.meta.isPrivate`);
            const duration = formatDuration(durationMinutes, durationUnit);

            return (
              <div
                key={field.id}
                style={{
                  border: isOpen
                    ? `1px solid ${colors.yellow}`
                    : "1px solid #ececec",
                  borderRadius: "12px",
                  overflow: "hidden",
                  transition: "border-color 0.15s ease",
                  boxShadow: isOpen ? "0 2px 10px rgba(0,0,0,0.05)" : "none",
                }}
              >
                <div
                  onClick={() => toggle(index)}
                  className="d-flex justify-content-between align-items-center px-3 py-3"
                  style={{
                    cursor: "pointer",
                    backgroundColor: isOpen ? "#fffbf0" : "#fafafa",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-3 flex-grow-1"
                    style={{ minWidth: 0 }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        backgroundColor: isOpen ? colors.yellow : "#eee",
                        color: isOpen ? "#fff" : "#888",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                      }}
                    >
                      {index + 1}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="fw-semibold text-truncate">
                        {title || `Option #${index + 1}`}
                      </div>
                      <div className="small text-muted text-truncate">
                        {duration ? `${duration} · ` : ""}
                        {SCHEDULE_MODE_LABELS[scheduleMode] ||
                          "Calendrier par défaut"}
                      </div>
                    </div>
                    <div className="d-flex gap-1 flex-shrink-0">
                      {wheelchairAccessible && (
                        <CBadge
                          shape="rounded-pill"
                          style={{ backgroundColor: "#eee", color: "#666" }}
                          title="Accessible en fauteuil roulant"
                        >
                          ♿
                        </CBadge>
                      )}
                      {isPrivate && (
                        <CBadge
                          shape="rounded-pill"
                          style={{ backgroundColor: "#eee", color: "#666" }}
                        >
                          Privée
                        </CBadge>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <CButton
                      size="sm"
                      variant="ghost"
                      color="danger"
                      onClick={(e) => handleRemove(e, index)}
                      title="Retirer cette option"
                    >
                      <DeleteIcon width="14" fill="#dc3545" />
                    </CButton>
                    <div style={{ color: "#999" }}>
                      <ChevronIcon open={isOpen} />
                    </div>
                  </div>
                </div>

                <CCollapse visible={isOpen}>
                  <div
                    className="p-3 pt-4"
                    style={{ borderTop: "1px solid #ececec" }}
                  >
                    <CRow>
                      <CCol md={4}>
                        <Field label="Titre" required>
                          <CFormInput {...register(`options.${index}.title`)} />
                        </Field>
                      </CCol>

                      <CCol md={2}>
                        <Field label="Durée">
                          <div className="d-flex gap-1">
                            <CFormInput
                              type="number"
                              min={0}
                              step="any"
                              {...register(`options.${index}.durationMinutes`, {
                                setValueAs: (value) =>
                                  value === "" ? null : Number(value),
                              })}
                            />
                            <CFormSelect
                              style={{ maxWidth: "70px" }}
                              {...register(`options.${index}.durationUnit`)}
                            >
                              <option value="min">min</option>
                              <option value="hr">hr</option>
                            </CFormSelect>
                          </div>
                        </Field>
                      </CCol>

                      <CCol md={3}>
                        <Field label="Annulation gratuite avant">
                          <div className="d-flex gap-1">
                            <CFormInput
                              type="number"
                              min={0}
                              step="any"
                              {...register(
                                `options.${index}.freeCancelThreshold`,
                                {
                                  setValueAs: (value) =>
                                    value === "" ? null : Number(value),
                                },
                              )}
                            />
                            <CFormSelect
                              style={{ maxWidth: "70px" }}
                              {...register(
                                `options.${index}.freeCancelThresholdUnit`,
                              )}
                            >
                              <option value="min">min</option>
                              <option value="hr">hr</option>
                            </CFormSelect>
                          </div>
                        </Field>
                      </CCol>

                      <CCol md={3}>
                        <Field label="Fermeture réservation avant">
                          <div className="d-flex gap-1">
                            <CFormInput
                              type="number"
                              min={0}
                              step="any"
                              {...register(
                                `options.${index}.bookingThreshold`,
                                {
                                  setValueAs: (value) =>
                                    value === "" ? null : Number(value),
                                },
                              )}
                            />
                            <CFormSelect
                              style={{ maxWidth: "70px" }}
                              {...register(
                                `options.${index}.bookingThresholdUnit`,
                              )}
                            >
                              <option value="min">min</option>
                              <option value="hr">hr</option>
                            </CFormSelect>
                          </div>
                        </Field>
                      </CCol>
                    </CRow>

                    <Field label="Description">
                      <CFormTextarea
                        rows={2}
                        {...register(`options.${index}.description`)}
                      />
                    </Field>

                    <div className="d-flex gap-3 mb-3">
                      <CFormCheck
                        label="Accessible en fauteuil roulant"
                        {...register(
                          `options.${index}.meta.wheelchairAccessible`,
                        )}
                      />
                      <CFormCheck
                        label="Privée"
                        {...register(`options.${index}.meta.isPrivate`)}
                      />
                      <CFormCheck
                        label="Recommandée"
                        {...register(`options.${index}.meta.isRecommended`)}
                      />
                    </div>

                    <div
                      className="mt-2 p-3"
                      style={{
                        border: "1px solid #ececec",
                        borderRadius: "12px",
                      }}
                    >
                      <div
                        className="fw-semibold mb-3"
                        style={{
                          fontSize: "0.85rem",
                          color: "#555",
                          letterSpacing: "0.02em",
                        }}
                      >
                        CALENDRIER DE DISPONIBILITÉ
                      </div>
                      <div className="d-flex gap-2 mb-3 flex-wrap">
                        {[
                          {
                            value: "default",
                            label: "Par défaut de l'activité",
                          },
                          {
                            value: "existing",
                            label: "Réutiliser un calendrier existant",
                          },
                          { value: "custom", label: "Calendrier personnalisé" },
                        ].map((opt) => (
                          <CButton
                            key={opt.value}
                            size="sm"
                            onClick={() =>
                              (document.activeElement as HTMLElement)?.blur()
                            }
                            style={{
                              backgroundColor:
                                scheduleMode === opt.value
                                  ? colors.yellow
                                  : "#f4f4f4",
                              color:
                                scheduleMode === opt.value ? "#fff" : "#333",
                              border: "none",
                            }}
                          >
                            <label
                              className="d-flex align-items-center gap-2 m-0"
                              style={{ cursor: "pointer" }}
                            >
                              <input
                                type="radio"
                                className="d-none"
                                value={opt.value}
                                {...register(`options.${index}.scheduleMode`)}
                              />
                              {opt.label}
                            </label>
                          </CButton>
                        ))}
                      </div>

                      {scheduleMode === "existing" && (
                        <div className="mb-2">
                          {reusableSchedules.length === 0 ? (
                            <div className="text-muted small">
                              Aucun calendrier enregistré n'est encore
                              disponible. Enregistrez d'abord un brouillon avec
                              vos calendriers (étape "Disponibilités") pour
                              pouvoir les réutiliser ici.
                            </div>
                          ) : (
                            <>
                              <Field label="Calendrier">
                                <CFormSelect
                                  {...register(`options.${index}.scheduleId`)}
                                >
                                  <option value="">Sélectionner...</option>
                                  {reusableSchedules.map((s: any) => (
                                    <option key={s.id} value={s.id}>
                                      {s.name}
                                    </option>
                                  ))}
                                </CFormSelect>
                              </Field>
                              <div className="small text-muted mt-1">
                                Ce calendrier est réutilisé tel quel. Pour le
                                modifier, faites-le depuis l'étape
                                "Disponibilités".
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {scheduleMode === "custom" && (
                        <div
                          className="p-3 mt-2"
                          style={{
                            border: "1px dashed #ddd",
                            borderRadius: "10px",
                          }}
                        >
                          <ScheduleEditor
                            namePrefix={`options.${index}.schedule`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CCollapse>
              </div>
            );
          })}
        </div>

        <CButton
          style={{
            backgroundColor: colors.yellow,
            border: "none",
            color: "#fff",
          }}
          size="sm"
          onClick={handleAdd}
        >
          <PlusIcon width="16" fill="#fff" /> Ajouter une option
        </CButton>
      </SectionCard>
    </div>
  );
}
